<template>
  <MarketplaceCard
    v-if="callRail.config"
    v-model="switchOn"
    :disabled="switchDisabled"
    :logo-path="imageUrl"
    logo-alt="CallRail"
    description="CallRail provides call analytics to data driven marketers so they can improve customer acquisition."
    learn-more-link="https://www.callrail.com/pricing/sera"
    cy-selector="marketplaceCallRail"
  >
    <template #configure>
      <div>
        <div class="link" @click="openModal">Set Up Information</div>
      </div>
    </template>
    <template #modal>
      <div>
        <Modal
          v-if="modalOpened"
          title="Set Up Information - CallRail"
          class-name="call-rail-config-modal"
          :submit-btn-text="modalButton"
          @submit="saveIntegration"
          @delete="dropIntegration"
          @hide-modal="closeModal"
        >
          <div class="d-flex flex-column">
            <InputField
              v-model="callRail.config.api_key"
              class="!mb-0"
              title="API Key"
              type="password"
              placeholder="API Key"
              toggleable
            />
            <Select
              v-if="accountVisible"
              v-model="callRail.config.account_id"
              name="Account"
              label="Account"
              full-width
              :options="callRail.accounts"
              class="mb-2"
            />
            <Select
              v-if="companyVisible"
              v-model="callRail.config.company_id"
              name="Company"
              label="Company"
              full-width
              :options="callRail.companies"
              class="mb-2"
            />
          </div>
        </Modal>
      </div>
    </template>
  </MarketplaceCard>
</template>

<script>
import { mapActions, mapState } from "vuex";

import Modal from "@/components/Modal.vue";
import InputField from "@/components/Seranova/InputField/InputField.vue";
import CallRailLogo from "@/images/callrail_logo.png";
import MarketplaceCard from "./MarketplaceCard.vue";

export default {
  components: {
    MarketplaceCard,
    Modal,
    InputField
  },
  data() {
    return {
      switchDisabled: null,
      switchOn: null,
      modalDropVisible: null,
      modalButton: null,
      modalOpened: false,
      accountVisible: null,
      companyVisible: null
    };
  },
  computed: {
    ...mapState(["callRail"]),
    imageUrl: {
      get() {
        return CallRailLogo;
      }
    }
  },
  watch: {
    switchOn: function (newValue, oldValue) {
      if (oldValue === null || newValue === oldValue) return;
      if (!this.callRail.config.ready) return;
      newValue ? this.enableIntegration() : this.disableIntegration();
    },
    "callRail.config.updated_at": function (newValue, oldValue) {
      if (newValue !== oldValue) this.refreshModal();
    },
    "callRail.config.persisted": function (newValue, oldValue) {
      if (oldValue && !newValue) this.closeModal();
    },
    "callRail.config.ready": function (newValue, oldValue) {
      if (oldValue === newValue) return;
      if (newValue) this.closeModal();
      this.refreshTools();
    },
    "callRail.config.enabled": function (newValue, oldValue) {
      if (oldValue !== newValue) this.refreshTools();
    },
    "callRail.config.api_key": function (newValue, oldValue) {
      if (oldValue !== null && newValue === null) this.refreshModal();
    },
    "callRail.config.account_id": function (newValue, oldValue) {
      if (oldValue !== null && newValue === null) this.refreshModal();
    },
    "callRail.config.company_id": function (newValue, oldValue) {
      if (oldValue !== null && newValue === null) this.refreshModal();
    }
  },
  mounted() {
    this.fetchCallRail().then(this.refreshTools);
  },
  methods: {
    ...mapActions(["fetchCallRail", "changeCallRail", "dropCallRail"]),
    openModal() {
      return this.fetchCallRail().then(() => {
        this.modalOpened = true;
        this.refreshModal();
      });
    },
    closeModal() {
      this.modalOpened = false;
      return this.fetchCallRail().then(() => {
        this.refreshTools();
      });
    },
    enableIntegration() {
      return this.changeCallRail({ enabled: true }).then(() => {
        window.notify.success("CallRail integration enabled!");
      });
    },
    disableIntegration() {
      return this.changeCallRail({ enabled: false }).then(() => {
        window.notify.success("CallRail integration disabled!");
      });
    },
    saveIntegration() {
      return this.changeCallRail(this.callRail.config).then(() => {
        if (this.callRail.warnings.length > 0)
          window.notify.error(this.callRail.warnings[0]);
        else window.notify.success("CallRail settings saved!");
      });
    },
    dropIntegration() {
      return this.closeModal().then(() => {
        if (this.callRail.config.persisted) {
          this.dropCallRail().then(() => {
            window.notify.success("CallRail settings deleted!");
          });
        } else {
          window.notify.error(
            "The integration has not been configured yet. There's nothing to delete"
          );
        }
      });
    },
    // Make the state consisent
    refreshTools() {
      this.switchOn = this.callRail.config.enabled;
      this.switchDisabled = !this.callRail.config.ready;
      if (this.switchDisabled) this.switchOn = false;
    },
    refreshModal() {
      if (!this.modalOpened) return;

      // remove blank values
      if (this.callRail.config.api_key === "")
        this.callRail.config.api_key = null;
      if (this.callRail.accounts === null) this.callRail.accounts = [];
      if (this.callRail.config.account_id === "")
        this.callRail.config.account_id = null;
      if (this.callRail.companies === null) this.callRail.companies = [];
      if (this.callRail.config.company_id === "")
        this.callRail.config.company_id = null;
      if (this.callRail.config.webhooks_integration_id === "")
        this.callRail.config.webhooks_integration_id = null;
      if (this.callRail.config.signature_key === "")
        this.callRail.config.signature_key = null;

      // clean dependent values
      if (this.callRail.config.api_key === null) this.callRail.accounts = [];
      if (this.callRail.accounts.length === 0)
        this.callRail.config.account_id = null;
      if (this.callRail.config.account_id === null)
        this.callRail.companies = [];
      if (this.callRail.companies.length === 0)
        this.callRail.config.company_id = null;
      if (this.callRail.config.company_id === null)
        this.callRail.config.webhooks_integration_id = null;
      if (this.callRail.config.webhooks_integration_id === null)
        this.callRail.config.signature_key = null;

      // update state
      this.accountVisible = this.callRail.config.api_key !== null;
      this.companyVisible = this.callRail.config.account_id !== null;
      this.modalButton =
        this.callRail.config.signature_key === null ? "Next" : "Save";
    }
  }
};
</script>
